import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { 
  Bot, Sparkles, CheckCircle2, ArrowRight, Zap, Target, 
  MessageSquare, Search, Mic, Globe, TrendingUp, Shield,
  Star, Users, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/lib/constants";
import QuoteRequestModal from "@/components/QuoteRequestModal";
import ReviewProofStrip from "@/components/ReviewProofStrip";
import CaseStudyProofLinks from "@/components/CaseStudyProofLinks";

const SEO = {
  title: "AEO & GEO Services Dallas | AI Search Optimization | Answer Engine & Generative Engine Optimization DFW",
  description: "Get found in ChatGPT, Google AI Overviews, Perplexity & voice search. Dallas AEO (Answer Engine Optimization) and GEO (Generative Engine Optimization) agency. Optimize your DFW business for AI-powered search today!",
  keywords: "AEO Dallas, GEO Dallas, Answer Engine Optimization Texas, Generative Engine Optimization DFW, ChatGPT optimization Dallas, AI search optimization, voice search SEO Dallas, Perplexity optimization, Google AI Overviews optimization, digital marketing consultant Dallas",
  image: "https://customer-assets.emergentagent.com/job_e227742f-fa2d-44b4-a974-4045826f8a41/artifacts/epvouypm_DMN%20Logo%20Final%20A%20%28Light%29%20transparent.PNG",
  url: "https://digitalmedianinja.com/aeo-geo"
};

// FAQ Data
const AEO_GEO_FAQS = [
  {
    id: "faq-1",
    question: "What is Answer Engine Optimization (AEO)?",
    answer: "AEO is the practice of optimizing your content to appear as direct answers in AI-powered search results, voice assistants (Siri, Alexa, Google Assistant), and featured snippets. Unlike traditional SEO that focuses on ranking websites, AEO focuses on becoming the definitive answer to user queries across AI platforms like ChatGPT, Perplexity, and Google AI Overviews."
  },
  {
    id: "faq-2",
    question: "What is Generative Engine Optimization (GEO)?",
    answer: "GEO is the practice of optimizing your digital presence to be cited and recommended by AI-powered generative search engines like ChatGPT, Google Bard, Perplexity AI, and Microsoft Copilot. GEO focuses on building the authority signals, structured data, and content quality that AI platforms rely on when generating responses."
  },
  {
    id: "faq-3",
    question: "Why do Dallas businesses need AEO and GEO?",
    answer: "40% of Gen Z prefers AI search over traditional Google, and ChatGPT has over 100 million weekly active users. AI Overviews now appear in 84% of informational queries. Dallas businesses that optimize for AI-powered search today will have a significant competitive advantage as AI search adoption continues to grow."
  },
  {
    id: "faq-4",
    question: "How is AEO different from traditional SEO?",
    answer: "Traditional SEO focuses on ranking web pages in search engine results pages (SERPs). AEO focuses on getting your content featured as direct answers in AI assistants, voice search, and featured snippets. While SEO drives clicks to your website, AEO positions your brand as the authoritative answer regardless of whether users visit your site."
  },
  {
    id: "faq-5",
    question: "How long does it take to see results from AEO/GEO?",
    answer: "Initial improvements in AI visibility can be seen within 4-8 weeks of implementing proper schema markup and content optimization. Significant improvements in AI citations and voice search visibility typically occur within 3-6 months."
  },
  {
    id: "faq-6",
    question: "What platforms does AEO optimization target?",
    answer: "AEO optimization targets ChatGPT, Google AI Overviews, Perplexity AI, Microsoft Copilot, Siri, Alexa, Google Assistant, and other AI-powered search and voice interfaces. We optimize your content to be the preferred answer source across all major AI platforms."
  },
  {
    id: "faq-7",
    question: "Do I still need traditional SEO if I do AEO/GEO?",
    answer: "Yes, traditional SEO remains important and works synergistically with AEO/GEO. Strong organic rankings and website authority contribute to AI platform trust signals. We recommend a comprehensive strategy that includes traditional SEO, local SEO, AEO, and GEO for maximum visibility."
  },
  {
    id: "faq-8",
    question: "What schema markup do you implement for AEO/GEO?",
    answer: "We implement FAQ schema, HowTo schema, Organization schema, LocalBusiness schema, Product schema, Service schema, Article schema, and specialized LLM-focused structured data. This comprehensive schema markup helps AI platforms understand, categorize, and cite your content accurately."
  },
  {
    id: "faq-9",
    question: "Do you use AI tools in your marketing work?",
    answer: "Yes — transparently. We use AI for research, data analysis, and testing how platforms like ChatGPT and Perplexity respond to queries about our clients. Strategy, copywriting, and quality control are always human-led by our Dallas team. We also engineer your content and structured data specifically so AI platforms can understand and cite your business."
  },
  {
    id: "faq-10",
    question: "What does LLM optimization include?",
    answer: "Our LLM optimization covers AI crawler access (robots.txt and llms.txt configuration), entity-consistent business information across the web, answer-first content formatting, FAQ/HowTo/Organization schema markup, citation-worthy content development, and monthly tracking of how often ChatGPT, Perplexity, Gemini, and Google AI Overviews mention your brand."
  },
  {
    id: "faq-11",
    question: "Can you guarantee my business will appear in ChatGPT or Google AI Overviews?",
    answer: "No agency can honestly guarantee AI citations — the platforms control their own outputs. What we do is systematically increase the probability: proper structure, authoritative content, consistent entity signals, and third-party mentions. We track citation frequency monthly so you can see progress, and our own site earns citations from ChatGPT, Perplexity, and Claude using the same playbook."
  }
];

const AEOGEOPage = () => {
  useEffect(() => {
    document.title = SEO.title;
  }, []);

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="aeo-geo-page">
      <Helmet>
        <title>{SEO.title}</title>
        <meta name="description" content={SEO.description} />
        <meta name="keywords" content={SEO.keywords} />
        <link rel="canonical" href={SEO.url} />
        <meta property="og:title" content={SEO.title} />
        <meta property="og:description" content={SEO.description} />
        <meta property="og:image" content={SEO.image} />
        <meta property="og:url" content={SEO.url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={SEO.title} />
        <meta name="twitter:description" content={SEO.description} />
      </Helmet>

      <SchemaMarkup faqs={AEO_GEO_FAQS} />
      <HeroSection />
      <StatsBar />
      <WhatIsSection />
      <WhyItMatters />
      <ProcessSection />
      <PricingSection />
      <CaseStudyProofLinks slugs={["ninjathlete", "trainagents"]} heading="AI search visibility, proven" />
      <ReviewProofStrip />
      <FAQSection faqs={AEO_GEO_FAQS} />
      <CTASection />
    </div>
  );
};

// Schema Markup Component
const SchemaMarkup = ({ faqs }) => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "AEO & GEO Optimization Services",
    "description": "Answer Engine Optimization and Generative Engine Optimization services for Dallas businesses",
    "provider": {
      "@type": "Organization",
      "name": "Digital Media Ninja"
    },
    "areaServed": {
      "@type": "City",
      "name": "Dallas"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "AEO GEO Plans",
      "itemListElement": [
        { "@type": "Offer", "name": "Starter", "price": "500", "priceCurrency": "USD" },
        { "@type": "Offer", "name": "Growth", "price": "1000", "priceCurrency": "USD" },
        { "@type": "Offer", "name": "Enterprise", "price": "1500", "priceCurrency": "USD" }
      ]
    }
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />
    </>
  );
};

// Hero Section
const HeroSection = () => (
  <section className="container-custom mb-20">
    <div className="grid lg:grid-cols-2 gap-16 items-center">
      <div className="space-y-8">
        <div className="flex gap-3">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00E676]/30 bg-[#00E676]/10">
            <Bot className="w-4 h-4 text-[#00E676]" />
            <span className="text-sm text-[#00E676] font-medium">AEO Services</span>
          </span>
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#6366F1]/30 bg-[#6366F1]/10">
            <Sparkles className="w-4 h-4 text-[#6366F1]" />
            <span className="text-sm text-[#6366F1] font-medium">GEO Services</span>
          </span>
        </div>

        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
          Dominate <span className="text-[#00E676]">AI-Powered Search</span> in Dallas
        </h1>

        <p className="text-xl text-gray-400">
          Get found in ChatGPT, Perplexity, Google AI Overviews, and voice assistants. 
          Our AEO and GEO services future-proof your digital presence for the AI search revolution.
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/contact">
            <Button className="btn-ninja px-8 py-6 text-lg">
              Get Free AI Visibility Audit
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <a href="#pricing">
            <Button className="btn-outline px-8 py-6 text-lg">View Pricing</Button>
          </a>
        </div>
      </div>

      <div className="relative">
        <div className="glass rounded-2xl p-8 space-y-6">
          <h3 className="text-2xl font-bold">AI Search Platforms We Optimize For:</h3>
          <PlatformsList />
        </div>
      </div>
    </div>
  </section>
);

// Platforms List
const PlatformsList = () => (
  <div className="grid grid-cols-2 gap-4">
    <PlatformItem icon={Bot} name="ChatGPT & GPT-4" />
    <PlatformItem icon={Search} name="Google AI Overviews" />
    <PlatformItem icon={Sparkles} name="Perplexity AI" />
    <PlatformItem icon={Globe} name="Microsoft Copilot" />
    <PlatformItem icon={Mic} name="Siri & Apple Intelligence" />
    <PlatformItem icon={MessageSquare} name="Alexa & Google Assistant" />
  </div>
);

const PlatformItem = ({ icon: Icon, name }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
    <Icon className="w-5 h-5 text-[#00E676]" />
    <span className="text-gray-300">{name}</span>
  </div>
);

// Stats Bar
const StatsBar = () => (
  <section className="border-y border-white/10 py-12 mb-20">
    <div className="container-custom">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        <StatItem icon={TrendingUp} value="40%" label="Gen Z prefers AI search" />
        <StatItem icon={Users} value="100M+" label="ChatGPT weekly users" />
        <StatItem icon={Search} value="84%" label="Queries show AI Overviews" />
        <StatItem icon={Target} value="25%" label="Traditional search decline by 2026" />
      </div>
    </div>
  </section>
);

const StatItem = ({ icon: Icon, value, label }) => (
  <div className="text-center">
    <Icon className="w-8 h-8 text-[#00E676] mx-auto mb-3" />
    <p className="text-4xl font-bold text-white mb-1">{value}</p>
    <p className="text-gray-500 text-sm">{label}</p>
  </div>
);

// What Is Section
const WhatIsSection = () => (
  <section className="container-custom mb-20">
    <div className="grid md:grid-cols-2 gap-12">
      <ServiceCard 
        icon={Bot}
        title="Answer Engine Optimization (AEO)"
        description="AEO optimizes your content to be the direct answer in AI assistants, voice search, and featured snippets. Instead of ranking #1, you become THE answer."
        features={["Voice search optimization", "Featured snippet targeting", "FAQ schema implementation", "Natural language content", "AI assistant visibility"]}
        color="#00E676"
      />
      <ServiceCard 
        icon={Sparkles}
        title="Generative Engine Optimization (GEO)"
        description="GEO ensures your business is cited and recommended by AI-powered generative search engines like ChatGPT, Perplexity, and Google SGE."
        features={["AI citation building", "Authority signal development", "LLM schema markup", "E-E-A-T optimization", "Generative AI monitoring"]}
        color="#6366F1"
      />
    </div>
  </section>
);

const ServiceCard = ({ icon: Icon, title, description, features, color }) => (
  <Card className="bg-[#0A0A0A] border-white/10 hover:border-white/20 transition-all">
    <CardHeader>
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: color + "20" }}>
        <Icon className="w-8 h-8" style={{ color }} />
      </div>
      <CardTitle className="text-2xl">{title}</CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <p className="text-gray-400">{description}</p>
      <ul className="space-y-3">
        {features.map((item, i) => (
          <li key={i} className="flex items-center gap-2 text-gray-300">
            <CheckCircle2 className="w-4 h-4" style={{ color }} />
            {item}
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

// Why It Matters
const WhyItMatters = () => (
  <section className="bg-[#0A0A0A] py-20 mb-20">
    <div className="container-custom">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <span className="text-[#00E676] uppercase tracking-wider text-sm font-medium">Why It Matters</span>
        <h2 className="text-4xl font-bold mt-4 mb-6">The Future of Search is AI-Powered</h2>
        <p className="text-gray-400 text-lg">
          Traditional search is evolving. Users increasingly turn to AI assistants for answers. 
          Is your Dallas business ready?
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <BenefitCard icon={Zap} title="Instant Answers" desc="AI provides immediate answers without users clicking through to websites. Be the source AI trusts." />
        <BenefitCard icon={Shield} title="Future-Proof Strategy" desc="As AI search grows, businesses optimized now gain lasting competitive advantages." />
        <BenefitCard icon={Target} title="Higher Intent Traffic" desc="Users who find you through AI citations are highly qualified and ready to convert." />
      </div>
    </div>
  </section>
);

const BenefitCard = ({ icon: Icon, title, desc }) => (
  <div className="text-center p-8 rounded-2xl bg-white/5 border border-white/10">
    <Icon className="w-12 h-12 text-[#00E676] mx-auto mb-4" />
    <h3 className="text-xl font-bold mb-3">{title}</h3>
    <p className="text-gray-400">{desc}</p>
  </div>
);

// Process Section
const ProcessSection = () => (
  <section className="container-custom mb-20">
    <div className="text-center max-w-3xl mx-auto mb-16">
      <span className="text-[#00E676] uppercase tracking-wider text-sm font-medium">Our Process</span>
      <h2 className="text-4xl font-bold mt-4 mb-6">How We Optimize for AI Search</h2>
    </div>

    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      <ProcessStep num={1} title="AI Visibility Audit" desc="We analyze how your brand appears across all AI platforms and identify opportunities." />
      <ProcessStep num={2} title="Schema Implementation" desc="Add FAQ, HowTo, and LLM-optimized schema markup to your website." />
      <ProcessStep num={3} title="Content Optimization" desc="Restructure content for Q&A format and natural language queries." />
      <ProcessStep num={4} title="Authority Building" desc="Develop E-E-A-T signals and get cited by authoritative sources." />
      <ProcessStep num={5} title="Voice Search Optimization" desc="Optimize for conversational queries and voice assistants." />
      <ProcessStep num={6} title="Monitor & Iterate" desc="Track AI citations and continuously improve your visibility." />
    </div>
  </section>
);

const ProcessStep = ({ num, title, desc }) => (
  <div className="flex gap-4">
    <div className="w-12 h-12 rounded-full bg-[#00E676]/20 flex items-center justify-center shrink-0 text-[#00E676] font-bold">
      {num}
    </div>
    <div>
      <h3 className="text-xl font-bold mb-2">{title}</h3>
      <p className="text-gray-400">{desc}</p>
    </div>
  </div>
);

// Pricing Section with A/B Testing
const PricingSection = () => {
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [abVariant, setAbVariant] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  // Get or create visitor ID for consistent A/B assignment
  const getVisitorId = () => {
    let visitorId = localStorage.getItem('dmn_visitor_id');
    if (!visitorId) {
      visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
      localStorage.setItem('dmn_visitor_id', visitorId);
    }
    return visitorId;
  };

  // Fetch A/B test variant on mount
  useEffect(() => {
    const fetchABTest = async () => {
      try {
        const visitorId = getVisitorId();
        const response = await axios.get(`${API}/ab-test/pricing?visitor_id=${visitorId}`);
        setAbVariant(response.data.variant);
        setPricing(response.data.pricing);
        
        // Track view event
        await axios.post(`${API}/ab-test/track`, { variant: response.data.variant, event_type: "view", visitor_id: visitorId });
      } catch (error) {
        console.error("A/B test fetch error:", error);
        // Fallback to default pricing
        setPricing({
          starter: { price: 500, cta: "Get Started", highlighted: false },
          growth: { price: 1000, cta: "Get Started - Most Popular", highlighted: true },
          enterprise: { price: 1500, cta: "Get Started", highlighted: false }
        });
      }
    };
    fetchABTest();
  }, []);

  const handlePayment = async (planId) => {
    setLoadingPlan(planId);
    try {
      const visitorId = getVisitorId();
      
      // Track click event
      if (abVariant) {
        await axios.post(`${API}/ab-test/track`, { variant: abVariant, event_type: "click", plan_id: planId, visitor_id: visitorId });
      }

      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/checkout`, {
        plan_id: planId,
        origin_url: originUrl,
        ab_variant: abVariant
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Unable to start checkout. Please try again or contact us.");
      setLoadingPlan(null);
    }
  };

  // Show loading state while fetching A/B variant
  if (!pricing) {
    return (
      <section id="pricing" className="bg-[#0A0A0A] py-20 mb-20">
        <div className="container-custom text-center">
          <div className="animate-pulse text-[#00E676]">Loading pricing...</div>
        </div>
      </section>
    );
  }

  return (
    <section id="pricing" className="bg-[#0A0A0A] py-20 mb-20">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#00E676] uppercase tracking-wider text-sm font-medium">Pricing</span>
          <h2 className="text-4xl font-bold mt-4 mb-6">Choose Your AI Optimization Plan</h2>
          <p className="text-gray-400 text-lg">
            Transparent pricing with no hidden fees. All plans include a free initial consultation.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <PricingCard 
            name="Starter"
            planId="starter"
            price={`$${pricing.starter.price}`}
            description="Perfect for small businesses starting with AI search optimization"
            features={["AI Visibility Audit", "Basic Schema Implementation", "FAQ Schema Markup", "Monthly Reporting", "1 Target Location", "Email Support"]}
            popular={pricing.starter.highlighted}
            ctaText={pricing.starter.cta}
            loading={loadingPlan === "starter"}
            onPayment={() => handlePayment("starter")}
          />
          <PricingCard 
            name="Growth"
            planId="growth"
            price={`$${pricing.growth.price}`}
            description="Ideal for growing businesses serious about AI dominance"
            features={["Everything in Starter", "Advanced Schema Markup", "Voice Search Optimization", "Content Optimization (5 pages)", "E-E-A-T Development", "Bi-weekly Reporting", "3 Target Locations", "Priority Support"]}
            popular={pricing.growth.highlighted}
            ctaText={pricing.growth.cta}
            loading={loadingPlan === "growth"}
            onPayment={() => handlePayment("growth")}
          />
          <PricingCard 
            name="Enterprise"
            planId="enterprise"
            price={`$${pricing.enterprise.price}`}
            description="Complete AI search domination for established businesses"
            features={["Everything in Growth", "Full LLM Schema Suite", "GEO Citation Building", "Content Optimization (15 pages)", "AI Monitoring Dashboard", "Competitor Analysis", "Weekly Strategy Calls", "Unlimited Locations", "Dedicated Account Manager"]}
            popular={pricing.enterprise.highlighted}
            ctaText={pricing.enterprise.cta}
            loading={loadingPlan === "enterprise"}
            onPayment={() => handlePayment("enterprise")}
          />
        </div>

        <p className="text-center text-gray-500 mt-8">
          All plans require a 3-month minimum commitment. Custom enterprise solutions available.
        </p>

        <div className="text-center mt-6">
          <p className="text-gray-400 mb-3">Need a custom solution tailored to your business?</p>
          <Button 
            variant="outline" 
            className="border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10"
            onClick={() => setShowQuoteModal(true)}
            data-testid="request-custom-quote-btn"
          >
            Request Custom Quote
          </Button>
        </div>
      </div>

      <QuoteRequestModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
        service="aeo-geo-services"
        serviceName="AEO & GEO Optimization Services"
        defaultBudget="5000-10000"
      />
    </section>
  );
};

const PricingCard = ({ name, planId, price, description, features, popular, ctaText, loading, onPayment }) => (
  <Card className={`bg-black border-2 transition-all relative ${popular ? "border-[#00E676] scale-105" : "border-white/10 hover:border-white/20"}`}>
    {popular && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
        <span className="bg-[#00E676] text-black px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
          <Star className="w-4 h-4" /> Most Popular
        </span>
      </div>
    )}
    <CardHeader className="text-center pt-8">
      <CardTitle className="text-xl mb-2">{name}</CardTitle>
      <div className="mb-4">
        <span className="text-5xl font-bold" style={{ color: popular ? "#00E676" : "white" }}>
          {price}
        </span>
        <span className="text-gray-500">/month</span>
      </div>
      <p className="text-gray-400 text-sm">{description}</p>
    </CardHeader>
    <CardContent className="space-y-6">
      <ul className="space-y-3">
        {features.map((feature, j) => (
          <li key={j} className="flex items-start gap-2 text-gray-300">
            <CheckCircle2 className="w-5 h-5 text-[#00E676] shrink-0 mt-0.5" />
            {feature}
          </li>
        ))}
      </ul>
      <Button 
        className={`w-full ${popular ? "btn-ninja" : "btn-outline"}`}
        onClick={onPayment}
        disabled={loading}
        data-testid={`pricing-btn-${planId}`}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          ctaText || (popular ? "Get Started - Most Popular" : "Get Started")
        )}
      </Button>
    </CardContent>
  </Card>
);

// FAQ Section
const FAQSection = ({ faqs }) => (
  <section className="container-custom mb-20">
    <div className="grid lg:grid-cols-2 gap-16">
      <div className="lg:sticky lg:top-32 lg:self-start">
        <span className="text-[#00E676] uppercase tracking-wider text-sm font-medium">FAQs</span>
        <h2 className="text-4xl font-bold mt-4 mb-6">Common Questions About AEO and GEO</h2>
        <p className="text-gray-400 text-lg mb-8">
          Get answers to frequently asked questions about Answer Engine Optimization 
          and Generative Engine Optimization services.
        </p>
        <Link to="/contact">
          <Button className="btn-ninja">
            <MessageSquare className="w-5 h-5 mr-2" />
            Ask Us Anything
          </Button>
        </Link>
      </div>

      <div>
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq) => (
            <AccordionItem 
              key={faq.id} 
              value={faq.id}
              className="border border-white/10 rounded-xl px-6 data-[state=open]:bg-white/5 transition-colors"
            >
              <AccordionTrigger className="text-left font-medium hover:text-[#00E676] py-5">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-gray-400 pb-5">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="py-20 bg-gradient-to-r from-[#00E676]/10 to-[#6366F1]/10">
    <div className="container-custom text-center">
      <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Dominate AI Search?</h2>
      <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10">
        Get your free AI visibility audit and discover how your Dallas business 
        can lead in the AI search revolution.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/contact">
          <Button className="btn-ninja px-10 py-6 text-lg">
            Get Your Free AI Audit
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
        <a href="tel:+14329349470">
          <Button className="btn-outline px-10 py-6 text-lg">Call (432) 934-9470</Button>
        </a>
      </div>
    </div>
  </section>
);

export default AEOGEOPage;
